// columnUtils.js
// Helpers to translate react-bootstrap-table-next style column definitions
// into @tanstack/react-table columnDefs, preserving the behaviors used
// across the app: isDummyField, hidden, formatter, headerStyle, align.

/**
 * Resolves the `hidden` prop of a legacy column definition.
 * Accepts either a boolean value (already evaluated by the caller,
 * e.g. `hidden: app.id !== undefined`) or a function `() => boolean`.
 */
export const isColumnHidden = (hidden) => {
    if (typeof hidden === 'function') {
        return !!hidden();
    }
    return !!hidden;
};

/**
 * Filters out columns marked as hidden, mirroring react-bootstrap-table-next
 * behavior where hidden columns are not rendered at all (not just visually hidden).
 */
export const getVisibleColumns = (columns = []) => {
    return columns.filter(col => !isColumnHidden(col.hidden));
};

/**
 * Builds a stable accessor key for a column. isDummyField columns (e.g. an
 * "actions" column) have no dataField, so we fall back to an index-based key.
 */
export const getColumnKey = (col, index) => {
    if (col.isDummyField) {
        return col.dataField || `__dummy_${index}`;
    }
    return col.dataField;
};

/**
 * Reads a (possibly nested) value out of a row using a dataField path.
 * Supports simple dot notation (e.g. "app.name") for parity with common usage.
 */
export const getCellValue = (row, dataField) => {
    if (!dataField) return undefined;
    if (!dataField.includes('.')) return row?.[dataField];
    return dataField.split('.').reduce((acc, key) => (acc == null ? acc : acc[key]), row);
};

/**
 * Translates a single legacy column definition into a TanStack-friendly
 * descriptor. We intentionally keep this format lightweight (not a full
 * @tanstack/react-table ColumnDef) since TableAdapter renders headers/cells
 * directly; this keeps formatter/isDummyField/align semantics identical to
 * the previous library without fighting TanStack's accessor model.
 */
export const toAdapterColumn = (col, index) => {
    const key = getColumnKey(col, index);
    return {
        id: key || `col_${index}`,
        dataField: col.dataField,
        text: col.text,
        isDummyField: !!col.isDummyField,
        headerStyle: col.headerStyle,
        align: col.align,
        headerAlign: col.headerAlign,
        formatter: col.formatter,
    };
};

/**
 * Builds the list of adapter-ready columns from the legacy `columns` prop,
 * already excluding hidden columns.
 */
export const buildAdapterColumns = (columns = []) => {
    return getVisibleColumns(columns).map(toAdapterColumn);
};

/**
 * Renders a cell value for a given adapter column + row, honoring formatter
 * when present (formatter signature kept as (cell, row) => ReactNode, same
 * as react-bootstrap-table-next).
 */
export const renderCell = (adapterColumn, row) => {
    const cellValue = adapterColumn.isDummyField
        ? undefined
        : getCellValue(row, adapterColumn.dataField);

    if (typeof adapterColumn.formatter === 'function') {
        return adapterColumn.formatter(cellValue, row);
    }
    return cellValue;
};
